import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useStateMethods, useStateSelector } from '../../providers/state';
import { selectImages, selectSelectedImageIndex } from '../../utils/selectors';
import { useEvents } from '../../providers/events';
import { Lightbox } from '../../../../modal_image';
import { createAttachmentUrl } from '../../utils/createAttachmentURL';
import { createNameWithExtension } from '../../utils/getAttachmentName';
import { stateKeys } from '../../constants/stateKeys';

const LightboxGroup = () => {
  const images = useStateSelector(selectImages);
  const currentIndex = useStateSelector(selectSelectedImageIndex);

  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState();

  const { getAttachment } = useEvents();

  const { setValue } = useStateMethods();

  const cache = useRef([]);

  useEffect(() => {
    let cancelled = false;
    const urlData = cache.current[currentIndex];

    if (urlData?.status === 'success') {
      setUrl(urlData.value);
      return;
    }

    setLoading(true);

    createAttachmentUrl(getAttachment, images[currentIndex].fileName).then(
      (urlData) => {
        if (cancelled) return;
        cache.current[currentIndex] = urlData;
        setUrl(urlData.value ?? '');

        if (urlData.status === 'error') {
          message.error(urlData.message);
        }

        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [currentIndex, getAttachment, images]);

  const onClose = () => {
    setValue(stateKeys.showLightbox, false);
  };

  useEffect(() => {
    const urlsData = cache.current;
    return () => {
      if (urlsData.length) {
        urlsData.forEach((urlData) => {
          if (typeof urlData?.removeUrl === 'function') {
            urlData.removeUrl();
          }
        });
      }

      cache.current = [];
    };
  }, []);

  const size = images.length;
  const { onKeyDown, onNext, onPrev } = useMemo(() => {
    return createHandlers(size, (cb) =>
      setValue(stateKeys.selectedImageIndex, cb)
    );
  }, [size, setValue]);

  return (
    <div ref={makeFocus} onKeyDown={onKeyDown} tabIndex={0}>
      <Lightbox
        loading={loading}
        medium={url}
        large={url}
        alt={createNameWithExtension(images[currentIndex])}
        onClose={onClose}
        onPrev={onPrev}
        onNext={onNext}
      />
    </div>
  );
};

export default LightboxGroup;

function makeFocus(node) {
  if (node) {
    node.focus();
  }
}

function createHandlers(size, setValue) {
  if (size <= 1) {
    return {
      onNext: undefined,
      onPrev: undefined,
      onKeyDown: undefined,
    };
  }

  const onNext = () => {
    setValue((prevIndex) => {
      const next = prevIndex + 1;
      return next < size ? next : 0;
    });
  };

  const onPrev = () => {
    setValue((prevIndex) => {
      const next = prevIndex - 1;
      return next >= 0 ? next : size - 1;
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      onNext();
    }
    if (e.key === 'ArrowLeft') {
      onPrev();
    }
  };

  return { onNext, onPrev, onKeyDown };
}
